import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { TextAreaWithAiAssistant } from '@client/components/_shared/ai/TextAreaWithAiAssistant';
import { UploadControlV2 } from '@client/components/_shared/documents/UploadControlV2';
import { CostCodeSelect } from '@client/components/_shared/selectors/financial/CostCodeSelect';
import { TransactionTypeSelect } from '@client/components/_shared/selectors/financial/TransactionTypeSelect';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { buildCurrencyInputRules } from '@client/utils';
import { createFileRoute } from '@tanstack/react-router';
import { Divider, Form } from 'antd';
import type { Decimal } from 'decimal.js';
import { useEffect, useId } from 'react';
import { claimFormStore } from '../../store';

export const Route = createFileRoute('/console/$orgId/claims/details/$id/financials/excesses/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Add excess',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ExcessAdd />
        </PageController>
    );
}

type Excess = {
    transactionTypeId: bigint;
    costCodeId: bigint;
    description: string;
    amount: Decimal;
};

const FormItem = Form.Item<Excess>;

function ExcessAdd() {
    const formId = useId();
    const isReadonly = claimFormStore.use.isReadonly();
    const claim = claimFormStore.use.formData();
    const { trpcUtils } = useTrpc();
    const { onFileListChange, files } = UploadControlV2.useUploadControlV2();
    const { mutate } = trpc.excess.createExcess.useMutation();
    const navigate = useNavigate();

    const [form] = Form.useForm<Excess>();

    useEffect(() => {
        if (isReadonly) {
            navigate({ moveInMatches: -1 });
        }
    }, [isReadonly]);

    if (isReadonly) {
        return null;
    }

    return (
        <OverlayPage
            title="Add excess"
            afterClose={() => {
                form.resetFields();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose }) => (
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" htmlType="submit" form={formId}>
                        Save
                    </Button>
                </>
            )}
        >
            {({ onClose }) => (
                <Form
                    form={form}
                    id={formId}
                    layout="vertical"
                    onFinish={(values) => {
                        message.loading('Saving...');

                        mutate(
                            {
                                description: values.description,
                                claimId: claim.id,
                                transactionTypeId: values.transactionTypeId,
                                costCodeId: values.costCodeId,
                                amount: values.amount,
                                files: files.map((item) => ({
                                    key: item.key,
                                    name: item.name,
                                    description: item.description,
                                    categories: item.categories,
                                    mimeType: item.mimeType,
                                    size: item.size,
                                    additionalInfo: item.additionalInfo,
                                })),
                            },
                            {
                                onSuccess() {
                                    message.success('Saved.');
                                    void trpcUtils.excess.invalidate();
                                    void trpcUtils.claim.getFinancialSummary.invalidate();
                                    onClose();
                                },
                                onError(error) {
                                    errorMessage.show(error);
                                },
                            },
                        );
                    }}
                >
                    {claim.productTypeId && (
                        <>
                            <FormItem label="Transaction type" name="transactionTypeId" rules={[{ required: true }]}>
                                <TransactionTypeSelect transactionNature="EXCESS" productTypeId={claim.productTypeId} />
                            </FormItem>
                            <FormItem label="Cost code" name="costCodeId" rules={[{ required: true }]}>
                                <CostCodeSelect productTypeId={claim.productTypeId} />
                            </FormItem>
                        </>
                    )}
                    <FormItem
                        label="Excess amount"
                        name="amount"
                        rules={buildCurrencyInputRules({ isRequired: true, gtZero: true })}
                    >
                        <CurrencyInput />
                    </FormItem>
                    <FormItem label="Description" name="description" rules={[{ required: true }]}>
                        <TextAreaWithAiAssistant relatedFieldLabel="Claim - Description" rows={4} />
                    </FormItem>

                    <Divider titlePlacement="start">Attachments</Divider>

                    <UploadControlV2
                        categories={['Excess']}
                        purpose="entityAttachment"
                        onFileListChange={onFileListChange}
                    />
                </Form>
            )}
        </OverlayPage>
    );
}
